import { BaseModel } from '@/models/BaseModel';
import useDevice, { DeviceData, getDeviceData } from '@/hooks/useDevice';
import { GameIndicator } from '@/models/GameModel';

enum GameIndicatorDirectionTypes {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal'
}

export enum GameIndicatorVAlignTypes {
  TOP = 'top',
  BOTTOM = 'bottom'
}

export enum GameIndicatorHAlignTypes {
  LEFT = 'left',
  RIGHT = 'right',
  CENTER = 'center'
}

enum GameIndicatorTypes {
  SCORE_USED = 'score_used',
  SCORE_LEFT = 'score_left',
  TIME_USED = 'time_used',
  TIME_LEFT = 'time_left',
  SPINS_LEFT = 'spins_left',
  SPINS_USED = 'spins_used',
  WINS_USED = 'wins_used',
  ROUND_USED = 'round_used',
  TRIES_USED = 'tries_used',
  TRIES_LEFT = 'tries_left',
  MOVES_USED = 'moves_used',
  MOVES_LEFT = 'moves_left'
}

enum GameIndicatorPositionTypes {
  TOP_LEFT = 'top-left',
  TOP_CENTER = 'top-center',
  TOP_RIGHT = 'top-right',
  CENTER_LEFT = 'center-left',
  CENTER_CENTER = 'center-center',
  CENTER_RIGHT = 'center-right',
  BOTTOM_LEFT = 'bottom-left',
  BOTTOM_CENTER = 'bottom-center',
  BOTTOM_RIGHT = 'bottom-right'
}
export enum GameIndicatorIcon {
  SPINS = 'spins',
  ROUNDS = 'rounds',
  WINS = 'wins',
  TRIES = 'tries',
  TIME = 'time',
  SCORE = 'score',
  MOVES = 'moves'
}

export interface GameIndicatorData {
  [key: string]: DeviceData<GameIndicatorTypeData>;
}

/**
 * Data is combined Layout and Indicators as they have to be handle the same way, and we can use generics.
 */
export interface GameIndicatorTypeData {
  enabled: boolean;
  type: GameIndicatorTypes;
  position?: GameIndicatorPositionTypes;
  value: number;
  label?: string;
  icon?: boolean;
  circle?: boolean;
  overwrite_tablet?: boolean;
  overwrite_mobile?: boolean;
  dynamic?: boolean;
  valign?: GameIndicatorVAlignTypes;
  halign?: GameIndicatorHAlignTypes;
  color?: string;
  direction?: GameIndicatorDirectionTypes;
}

export interface GameIndicatorState {
  layout?: GameIndicatorLayoutState;
  indicators: GameIndicatorTypeState[];
  globalStyles: string;
  containerClasses: ContainerClasses;
}

export interface GameIndicatorTypeState {
  enabled: boolean;
  type: GameIndicatorTypes;
  value: number;
  key: string;
  position?: GameIndicatorPositionTypes;
  label?: string;
  icon?: GameIndicatorIcon;
  circle?: boolean;
  dynamic?: boolean;
  valign?: GameIndicatorVAlignTypes;
  halign?: GameIndicatorHAlignTypes;
}

export interface GameIndicatorLayoutState {
  color?: string;
  direction?: GameIndicatorDirectionTypes;
}

interface ContainerClasses {
  [key: string]: boolean;
}

export class GameIndicatorSettingsModel extends BaseModel<GameIndicatorData, GameIndicatorState> {
  parse() {
    const state = this.state;
    state.indicators = [];
  }

  private static constructIndicatorsState(
    data: GameIndicatorTypeData,
    gameData: GameIndicator
  ): GameIndicatorTypeState {
    return {
      enabled: data.enabled || false,
      type: data.type,
      ...(data.position && { position: data.position }),
      ...(data.label && { label: data.label }),
      ...(data.circle && { circle: data.circle }),
      ...(data.dynamic && { dynamic: data.dynamic }),
      ...(data.valign && { valign: data.valign }),
      ...(data.halign && { halign: data.halign }),
      ...(data.icon && { icon: gameData.icon }),
      value: data.value,
      key: gameData.metricKey[data.type]
    };
  }
  private static constructLayoutState(data: GameIndicatorTypeData): GameIndicatorLayoutState | undefined {
    return {
      ...(data.color && { color: data.color }),
      ...(data.direction && { direction: data.direction })
    };
  }

  private static setIndicatorGlobalStyles(layout: GameIndicatorLayoutState) {
    return `body .game-indicator .game-indicator-item__icon-path,
            body .game-indicator .game-indicator-item__icon-path path,
            body .game-indicator .game-indicator-item__icon-path--dice circle {
              fill: ${layout.color};
            }
            body .game-indicator .game-indicator-item__icon-path--dice rect,
            body .game-indicator .game-indicator-item__circle-path {
              stroke: ${layout.color};
            }
            body .game-indicator .game-indicator-item::before {
              background-color: ${layout.color};
            }
            body .game-indicator .game-indicator-item {
              color: ${layout.color};
            }
    `;
  }

  private static containerClasses(state: GameIndicatorState): ContainerClasses {
    let hasTopPosition = false;
    let hasBottomPosition = false;
    let sameVAlign = true;
    let sameHAlign = true;
    let prevVAlign: string | undefined;
    let prevHAlign: string | undefined;
    let hasIcon = false;

    const direction = state.layout?.direction || GameIndicatorDirectionTypes.HORIZONTAL;

    const { isDesktop } = useDevice();

    state.indicators.forEach((indicator) => {
      /**
       * Its important to look at the last indicator, otherwise we just overwrite the values we need to set.
       * if the next is false, and the frist is true. this is the reason for looking at the previus.
       */
      if (prevVAlign && indicator.valign !== prevVAlign) {
        sameVAlign = false;
      }

      if (prevHAlign && indicator.halign !== prevHAlign) {
        sameHAlign = false;
      }
      if (indicator.icon && !hasIcon) {
        hasIcon = true;
      }

      if (isDesktop) {
        if (indicator.halign === GameIndicatorHAlignTypes.CENTER) {
          hasTopPosition = indicator.valign === GameIndicatorVAlignTypes.TOP;
          hasBottomPosition = indicator.valign === GameIndicatorVAlignTypes.BOTTOM;
        }
      } else {
        if (indicator.valign === 'top') {
          hasTopPosition = true;
        } else {
          hasBottomPosition = true;
        }
      }

      prevVAlign = indicator.valign;
      prevHAlign = indicator.halign;
    });

    return {
      'game-indicator--with-icon': hasIcon,
      [`game-indicator--align-${prevVAlign}-${prevHAlign}`]: sameVAlign && sameHAlign,
      [`game-indicator--${direction}`]: true,
      'game-indicator--fill-above': hasTopPosition,
      'game-indicator--fill-below': hasBottomPosition
    };
  }

  setAvailableIndicators(indicators: GameIndicator[]): void {
    if (this.getData().layout) {
      const data = getDeviceData<GameIndicatorTypeData>(this.getData().layout);
      if (data) {
        this.state.layout = GameIndicatorSettingsModel.constructLayoutState(data);
        if (this.state.layout?.color) {
          this.state.globalStyles = GameIndicatorSettingsModel.setIndicatorGlobalStyles(this.state.layout);
        }
      }
    }

    indicators.forEach((indicator) => {
      const indicatorData = this.getData()[indicator.indicatorKey];
      // data looks a bit different when not enabled and desktop object is the one that defines thats it actually enabled.
      if (indicatorData && indicatorData.desktop) {
        const data = getDeviceData<GameIndicatorTypeData>(indicatorData);

        if (data) {
          const indicatorItem = GameIndicatorSettingsModel.constructIndicatorsState(data, indicator);

          this.state.indicators.push(indicatorItem);
        }
      }
    });
    this.state.containerClasses = GameIndicatorSettingsModel.containerClasses(this.state);
  }

  authorSignature(): string {
    return 'Sebastian Jakobsen | Jannik Fischer';
  }
}
